import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useMemo, useState } from 'react';

import { TraceEvent } from '@/types';
import { formatDate } from '@/utils/date.util';

import { WorkflowStatusChip } from './WorkflowStatusChip';

export interface TraceNode {
  spanId: string;
  type: 'agent' | 'job' | 'task' | 'unknown';
  label: string;
  status: 'running' | 'pass' | 'fail' | 'queued';
  children: TraceNode[];
  created: string;
  parentSpanId?: string;
  rawName: string;
  target?: string;
  durationMs?: number;
  summary?: string; // AI-generated summary (at start - what we're asking for)
  description?: string; // AI-generated description (at end - what was done)
}

interface TraceHierarchyProps {
  traces: TraceEvent[];
  /** Enable entry animations for new nodes */
  animateEntries?: boolean;
}

// Parse the created field which may have format "#trace#2026-01-28T12:02:51.588324Z"
function parseCreatedTimestamp(created: string | undefined | null): string {
  if (!created) {
    return new Date().toISOString();
  }
  const cleanedTime = created.includes('#')
    ? created.split('#').filter(Boolean).pop() || created
    : created;
  return cleanedTime.replace(/\.\d{4,}Z$/, match => {
    const ms = match.slice(1, 4);
    return `.${ms}Z`;
  });
}

function parseCreatedTime(created: string | undefined | null): Date {
  return new Date(parseCreatedTimestamp(created));
}

function formatRelativeTime(created: string | undefined | null): string {
  const timestamp = parseCreatedTimestamp(created);
  return formatDate(timestamp);
}

function getTarget(event: TraceEvent): string | undefined {
  const target = event.attributes?.target;
  const identifier =
    event.attributes?.target_identifier ?? event.attributes?.target_name; // fallback for legacy
  const group = event.attributes?.target_group;

  // Webpages: combine group (domain) + identifier (path) for readable display
  // Target key format: #webpage#example.com#/path
  if (target?.startsWith('#webpage#') && group) {
    return `${group}${identifier}`;
  }

  // Default: just return identifier
  return identifier;
}

// Timeline marker glyph. Sits inside a 14px circle that punches through the
// spine (bg-chariot-background-primary) — see the .tl-marker wrapper in
// TraceNodeView. Running = pulsing brand dot (8px); queued = hollow stroke
// ring (10px); pass = check; fail = X. (design-contract §3)
function StatusIndicator({
  status,
}: {
  status: 'running' | 'pass' | 'fail' | 'queued';
}) {
  switch (status) {
    case 'pass':
      return <CheckIcon className="text-chariot-stroke-light-green size-3.5" />;
    case 'fail':
      return <XMarkIcon className="text-chariot-stroke-critical size-3.5" />;
    case 'running':
      return (
        <div className="bg-chariot-brand-primary size-2 animate-pulse rounded-full" />
      );
    case 'queued':
    default:
      return (
        <div className="border-chariot-stroke size-2.5 rounded-full border-2" />
      );
  }
}

// Workflow node status → shared chip with a capitalized timeline label.
const TRACE_CHIP_LABELS: Record<
  'running' | 'pass' | 'fail' | 'queued',
  string
> = {
  running: 'Running',
  pass: 'Pass',
  fail: 'Fail',
  queued: 'Queued',
};

function TraceStatusChip({
  status,
}: {
  status: 'running' | 'pass' | 'fail' | 'queued';
}) {
  return (
    <WorkflowStatusChip status={status} label={TRACE_CHIP_LABELS[status]} />
  );
}

function ElapsedTime({ created }: { created: string }) {
  const [elapsed, setElapsed] = useState('');

  useEffect(() => {
    const startTime = parseCreatedTime(created).getTime();

    const updateElapsed = () => {
      const now = Date.now();
      const diffMs = now - startTime;
      const diffSec = Math.floor(diffMs / 1000);
      const diffMin = Math.floor(diffSec / 60);
      const remainingSec = diffSec % 60;

      if (diffMin > 0) {
        setElapsed(`${diffMin}m ${remainingSec}s`);
      } else {
        setElapsed(`${diffSec}s`);
      }
    };

    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    return () => clearInterval(interval);
  }, [created]);

  // A11y: the running elapsed-time value must use the brighter success token
  // (#4ADE80, ≈8:1 on #0D0D0D). The previous `text-chariot-stroke-info`
  // (#145C45, ≈1.6:1) failed WCAG AA. See design-contract §0/§3.
  return <span className="text-success">{elapsed}</span>;
}

interface TraceNodeViewProps {
  node: TraceNode;
  depth?: number;
  isLast?: boolean;
  animate?: boolean;
}

// Strip markdown formatting from text
function stripMarkdown(text: string): string {
  return text
    .replace(/\*\*([^*]+)\*\*/g, '$1') // **bold**
    .replace(/\*([^*]+)\*/g, '$1') // *italic*
    .replace(/__([^_]+)__/g, '$1') // __bold__
    .replace(/_([^_]+)_/g, '$1') // _italic_
    .replace(/`([^`]+)`/g, '$1') // `code`
    .replace(/^#{1,6}\s+/gm, '') // # headers
    .replace(/^\s*[-*+]\s+/gm, '') // - bullet points
    .replace(/^\s*\d+\.\s+/gm, '') // 1. numbered lists
    .trim();
}

function TraceNodeView({
  node,
  depth = 0,
  isLast = true,
  animate = false,
}: TraceNodeViewProps) {
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const relativeTime = formatRelativeTime(node.created);
  const durationStr =
    node.durationMs !== undefined ? `(${node.durationMs}ms)` : '';

  // Use summary as primary label if available, original label as secondary
  const primaryLabel = node.summary ? stripMarkdown(node.summary) : node.label;
  const secondaryLabel = node.summary ? node.label : undefined;

  // Strip markdown from description
  const cleanDescription = node.description
    ? stripMarkdown(node.description)
    : undefined;

  // Vertical timeline rail (design-contract §3). Each node is a row with a
  // status marker on a continuous spine; nesting indents the spine.
  // The spine renders only when this node is followed by another at the same
  // level (i.e. not the last sibling) — connecting this marker down to the
  // next one. Children render their own (further-indented) spine.
  const showSpine = !isLast || node.children.length > 0;
  const isChild = depth > 0;

  const content = (
    <>
      <div className={`relative pb-3.5 ${isChild ? 'ml-[22px]' : ''}`}>
        {/* Spine: 1px vertical line connecting this marker to the next. */}
        {showSpine && (
          <div className="bg-chariot-stroke absolute top-4 -bottom-0.5 left-1.5 w-px" />
        )}

        {/* Marker: 14px circle punching through the spine. */}
        <div className="bg-chariot-background-primary absolute top-[3px] left-0 flex size-3.5 items-center justify-center rounded-full">
          <StatusIndicator status={node.status} />
        </div>

        {/* Content */}
        <div className="hover:bg-chariot-background-secondary hover:border-chariot-stroke ml-[26px] rounded-md border border-transparent py-0.5 pr-2 pl-1 transition-colors">
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              {/* Primary label with optional expand chevron for description */}
              <div className="text-chariot-text-primary flex items-center gap-1 text-xs font-medium">
                {cleanDescription && (
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      setIsDescriptionExpanded(!isDescriptionExpanded);
                    }}
                    className="text-chariot-text-tertiary hover:text-chariot-text-secondary flex-shrink-0 cursor-pointer transition-transform duration-150"
                    style={{
                      transform: isDescriptionExpanded
                        ? 'rotate(90deg)'
                        : 'rotate(0deg)',
                    }}
                    title={
                      isDescriptionExpanded ? 'Hide details' : 'Show details'
                    }
                  >
                    <svg
                      className="size-3"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                )}
                <span
                  className={`truncate ${
                    isChild
                      ? 'text-chariot-text-secondary font-normal'
                      : 'text-chariot-text-primary font-medium'
                  }`}
                  title={primaryLabel}
                >
                  {primaryLabel}
                </span>
              </div>
              {/* Secondary italic label (raw capability/agent name) */}
              {secondaryLabel && (
                <div
                  className={`text-chariot-text-tertiary truncate text-[10px] italic ${cleanDescription ? 'ml-4' : ''}`}
                  title={secondaryLabel}
                >
                  {secondaryLabel}
                </div>
              )}
              {/* Description block (revealed on expand) */}
              {cleanDescription && isDescriptionExpanded && (
                <div className="text-chariot-text-tertiary border-chariot-stroke bg-chariot-background-secondary mt-1.5 ml-4 rounded border-l-2 px-2 py-1.5 text-[10px] leading-relaxed">
                  {cleanDescription}
                </div>
              )}
              {/* Time / elapsed + duration */}
              <div
                className={`text-chariot-text-tertiary mt-0.5 text-[10px] ${cleanDescription ? 'ml-4' : ''}`}
              >
                {node.status === 'running' ? (
                  <>
                    Elapsed <ElapsedTime created={node.created} />
                  </>
                ) : (
                  relativeTime
                )}
                {durationStr && (
                  <span className="text-chariot-text-tertiary ml-1 font-mono">
                    {durationStr}
                  </span>
                )}
              </div>
            </div>
            <div className="flex-shrink-0">
              <TraceStatusChip status={node.status} />
            </div>
          </div>
        </div>
      </div>
      {node.children.length > 0 && (
        <div className="relative">
          {node.children.map((child, index) => (
            <TraceNodeView
              key={child.spanId}
              node={child}
              depth={depth + 1}
              isLast={index === node.children.length - 1}
              animate={animate}
            />
          ))}
        </div>
      )}
    </>
  );

  if (animate && depth === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
      >
        {content}
      </motion.div>
    );
  }

  return content;
}

/**
 * Builds trace hierarchy from flat trace events.
 */
function buildHierarchy(traces: TraceEvent[]): TraceNode[] {
  if (!traces || traces.length === 0) {
    return [];
  }

  const spanGroups = new Map<string, TraceEvent[]>();
  traces.forEach(event => {
    if (!event || !event.span_id) return;
    const existing = spanGroups.get(event.span_id) || [];
    existing.push(event);
    spanGroups.set(event.span_id, existing);
  });

  const allNodes = new Map<string, TraceNode>();
  spanGroups.forEach((events, spanId) => {
    if (!events || events.length === 0) return;

    const queuedEvent = events.find(e => e?.event_type?.endsWith('_queued'));
    const startEvent = events.find(e => e?.event_type?.endsWith('_start'));
    const endEvent = events.find(e => e?.event_type?.endsWith('_end'));
    const anyEvent = queuedEvent || startEvent || endEvent || events[0];

    if (!anyEvent || !anyEvent.event_type) return;

    let type: TraceNode['type'] = 'unknown';
    if (anyEvent.event_type.startsWith('agent_')) type = 'agent';
    else if (anyEvent.event_type.startsWith('job_')) type = 'job';
    else if (anyEvent.event_type.startsWith('task_')) type = 'task';

    let status: TraceNode['status'] = 'queued';
    if (endEvent) {
      const eventStatus = endEvent.attributes?.status;
      status = eventStatus === 'JP' || eventStatus === 'Pass' ? 'pass' : 'fail';
    } else if (startEvent) {
      status = 'running';
    }

    const target = getTarget(anyEvent);
    const durationMs = endEvent?.attributes?.duration_ms
      ? parseInt(endEvent.attributes.duration_ms, 10)
      : undefined;
    // Summary is generated at start from user prompt (describes what we're asking for)
    const summary = startEvent?.attributes?.summary;
    // Description is generated at end from conversation (describes what was done)
    const description = endEvent?.attributes?.description;

    let rawName = 'Unknown';
    if (type === 'agent') {
      rawName = anyEvent.attributes?.agent_name ?? 'Unknown';
    } else if (type === 'job') {
      rawName = anyEvent.attributes?.capability ?? 'Unknown';
    } else if (type === 'task') {
      rawName = anyEvent.attributes?.task_name ?? 'Unknown';
    }

    const parentSpanId = events.find(e => e.parent_span_id)?.parent_span_id;

    allNodes.set(spanId, {
      spanId,
      type,
      label: rawName,
      status,
      children: [],
      created: anyEvent.created,
      parentSpanId,
      rawName,
      target,
      durationMs,
      summary,
      description,
    });
  });

  const generateLabel = (node: TraceNode, parent: TraceNode | null): string => {
    const targetSuffix = node.target ? ` on ${node.target}` : '';

    if (!parent) {
      if (node.type === 'job') {
        return `Planner created ${node.rawName} job${targetSuffix}`;
      } else if (node.type === 'agent') {
        return `Planner invoked ${node.rawName}${targetSuffix}`;
      }
      return node.rawName;
    }

    if (parent.type === 'job' && node.type === 'agent') {
      return `Job ${parent.rawName} invoked ${node.rawName}${targetSuffix}`;
    } else if (parent.type === 'agent' && node.type === 'job') {
      return `Agent ${parent.rawName} created ${node.rawName} job${targetSuffix}`;
    } else if (parent.type === 'agent' && node.type === 'agent') {
      return `Agent ${parent.rawName} invoked ${node.rawName}${targetSuffix}`;
    } else if (parent.type === 'job' && node.type === 'job') {
      return `Job ${parent.rawName} spawned ${node.rawName} job${targetSuffix}`;
    }

    return node.rawName;
  };

  const rootNodes: TraceNode[] = [];
  allNodes.forEach(node => {
    if (node.parentSpanId && allNodes.has(node.parentSpanId)) {
      const parent = allNodes.get(node.parentSpanId)!;
      node.label = generateLabel(node, parent);
      parent.children.push(node);
    } else {
      node.label = generateLabel(node, null);
      rootNodes.push(node);
    }
  });

  // Fix parent status inference
  allNodes.forEach(node => {
    if (node.parentSpanId && allNodes.has(node.parentSpanId)) {
      const parent = allNodes.get(node.parentSpanId)!;
      if (
        parent.status === 'queued' &&
        (node.status === 'running' ||
          node.status === 'pass' ||
          node.status === 'fail')
      ) {
        parent.status = 'running';
      }
    }
  });

  // Sort nodes by created timestamp (oldest first at top)
  const sortNodes = (nodes: TraceNode[]) => {
    nodes.sort(
      (a, b) =>
        parseCreatedTime(a.created).getTime() -
        parseCreatedTime(b.created).getTime()
    );
    nodes.forEach(node => sortNodes(node.children));
  };
  sortNodes(rootNodes);

  return rootNodes;
}

/**
 * TraceHierarchy - Renders trace events as a hierarchical tree.
 * Extracted from ConversationTraces for reuse in TraceDrawer.
 */
export const TraceHierarchy: React.FC<TraceHierarchyProps> = ({
  traces,
  animateEntries = false,
}) => {
  const hierarchy = useMemo(() => buildHierarchy(traces), [traces]);

  if (hierarchy.length === 0) {
    return (
      <div className="text-chariot-text-secondary p-4 text-center text-sm">
        Workflow actions taken by the assistant will be displayed here
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <AnimatePresence>
        {hierarchy.map((node, index) => (
          <TraceNodeView
            key={node.spanId}
            node={node}
            isLast={index === hierarchy.length - 1}
            animate={animateEntries}
          />
        ))}
      </AnimatePresence>
    </div>
  );
};

export default TraceHierarchy;
